import { useState, useEffect, useRef } from 'react';
import { Musikk, MusikkFil, musikkAPI } from '@/services/api';

type FilType = 'upload' | 'url';

interface UseFilesTabProps {
  musikk: Musikk | null;
}

export const useFilesTab = ({ musikk }: UseFilesTabProps) => {
  const [navn, setNavn] = useState('');
  const [filType, setFilType] = useState<FilType>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [url, setUrl] = useState('');
  const [existingFiler, setExistingFiler] = useState<MusikkFil[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadingId, setUploadingId] = useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ navn?: string; fil?: string; url?: string }>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<MusikkFil | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (musikk?.id) {
      loadFiles();
    }
  }, [musikk?.id]);

  const loadFiles = async () => {
    if (!musikk?.id) return;
    
    try {
      setLoading(true);
      const response = await musikkAPI.getFiles(musikk.id);
      setExistingFiler(response.filer);
    } catch (error) {
      console.error('Kunne ikke laste filer:', error);
      setErrorMessage('Kunne ikke laste filer');
      setTimeout(() => setErrorMessage(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Sjekk filstørrelse (maks 100MB)
      if (file.size > 100 * 1024 * 1024) {
        setErrors({ ...errors, fil: 'Filen er for stor. Maks størrelse er 100MB.' });
        setSelectedFile(null);
        return;
      }
      
      // Sjekk filtype (audio filer)
      const allowedTypes = [
        'audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 
        'audio/aac', 'audio/flac', 'audio/m4a', 'audio/x-m4a'
      ];
      if (!allowedTypes.includes(file.type)) {
        setErrors({ ...errors, fil: 'Ugyldig filtype. Kun lydfiler er tillatt.' });
        setSelectedFile(null);
        return;
      }
      
      setSelectedFile(file);
      setErrors({ ...errors, fil: undefined });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { navn?: string; fil?: string; url?: string } = {};
    
    if (!navn.trim()) {
      newErrors.navn = 'Navn er påkrevd';
    }
    
    if (filType === 'upload' && !selectedFile) {
      newErrors.fil = 'Velg en fil å laste opp';
    }
    
    if (filType === 'url' && !url.trim()) {
      newErrors.url = 'URL er påkrevd';
    }
    
    if (filType === 'url' && url.trim()) {
      try {
        new URL(url);
      } catch {
        newErrors.url = 'Ugyldig URL-format';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddFile = async () => {
    if (!musikk?.id) return;
    if (!validateForm()) return;
    
    try {
      setLoading(true);

      if (filType === 'upload') {
        await musikkAPI.uploadFile(musikk.id, selectedFile!, navn.trim());
      } else {
        await musikkAPI.createUrlFile(musikk.id, navn.trim(), filType, url.trim());
      }
      
      setSuccessMessage('Fil lagret!');
      setTimeout(() => setSuccessMessage(null), 3000);
      
      // Reset skjema
      setNavn('');
      setSelectedFile(null);
      setUrl('');
      setFilType('upload');
      setErrors({});
      
      await loadFiles();
    } catch (error: any) {
      console.error('Kunne ikke lagre fil:', error);
      setErrorMessage(error.response?.data?.error || 'Kunne ikke lagre fil');
      setTimeout(() => setErrorMessage(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFile = async (fil: MusikkFil) => {
    setFileToDelete(fil);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteFile = async () => {
    if (!musikk?.id || !fileToDelete) return;
    
    try {
      setUploadingId(fileToDelete.id);
      setDeleteDialogOpen(false);
      await musikkAPI.deleteFile(musikk.id, fileToDelete.id);
      setSuccessMessage('Fil slettet');
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadFiles();
    } catch (error: any) {
      console.error('Kunne ikke slette fil:', error);
      setErrorMessage(error.response?.data?.error || 'Kunne ikke slette fil');
      setTimeout(() => setErrorMessage(null), 3000);
    } finally {
      setUploadingId(null);
      setFileToDelete(null);
    }
  };

  const handleDownload = async (fil: MusikkFil) => {
    if (!musikk?.id) return;
    
    try {
      const response = await musikkAPI.getFileUrl(musikk.id, fil.id);
      window.open(response.url, '_blank');
    } catch (error: any) {
      console.error('Kunne ikke hente nedlastingslenke:', error);
      setErrorMessage(error.response?.data?.error || 'Kunne ikke hente nedlastingslenke');
      setTimeout(() => setErrorMessage(null), 3000);
    }
  };

  const resetForm = () => {
    setNavn('');
    setSelectedFile(null);
    setUrl('');
    setFilType('upload');
    setErrors({});
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return {
    // State
    navn,
    setNavn,
    filType,
    setFilType,
    selectedFile,
    setSelectedFile,
    url,
    setUrl,
    existingFiler,
    loading,
    uploadingId,
    successMessage,
    setSuccessMessage,
    errorMessage,
    setErrorMessage,
    errors,
    setErrors,
    deleteDialogOpen,
    setDeleteDialogOpen,
    fileToDelete,
    fileInputRef,
    // Actions
    handleFileChange,
    handleAddFile,
    handleDeleteFile,
    confirmDeleteFile,
    handleDownload,
    resetForm,
    loadFiles,
  };
};

